/* eslint-disable @typescript-eslint/no-namespace */
import moment, { Moment } from 'moment';
import {
  DealVersion,
  DealType,
  OrderPeriodType,
  OrderPeriodOrderType
} from '../../types/deal.types';
import { toMoment } from '../../utils/converters/date';
import { IssueDate } from './date';

export namespace IssueOrderPeriod {
  export interface OrderPeriodStatus {
    isOpen: boolean;
    isExpiring: boolean;
    isDateOverCountdown: boolean;
  }

  export interface OrderPeriodCountdown extends OrderPeriodStatus {
    countdown: number;
    type: OrderPeriodType;
    orderTypes: OrderTypesType[];
  }

  interface OrderTypesType {
    orderType: string;
    priority: number;
  }

  namespace DealOrderType {
    export function getIssueOrderPeriodCountdown(
      issue: DealVersion,
      today: Moment
    ): OrderPeriodCountdown {
      const {
        isOpen,
        isExpiring,
        isDateOverCountdown
      } = isIssueOrderPeriodOpen(issue, today);
      const type: OrderPeriodType =
        (issue.orderPeriod && issue.orderPeriod.type) ||
        (issue.repricingOrderPeriod && issue.repricingOrderPeriod.type);
      const orderTypes: OrderPeriodOrderType[] =
        issue.orderPeriod && issue.orderPeriod.orderTypes
          ? issue.orderPeriod.orderTypes
          : null;

      return isOpen
        ? {
          isOpen,
          isExpiring,
          isDateOverCountdown,
          countdown: toCountdown(
            toMoment(
              (issue.orderPeriod &&
                today.diff(issue.orderPeriod.endDate) < 0 &&
                issue.orderPeriod.endDate) ||
              (issue.repricingOrderPeriod &&
                issue.repricingOrderPeriod.endDate)
            ),
            today
          ),
          type,
          orderTypes
        }
        : {
          isOpen,
          isExpiring,
          isDateOverCountdown,
          countdown: undefined,
          type,
          orderTypes
        };
    }

    export function isIssueOrderPeriodOpen(
      issue: DealVersion,
      today: Moment
    ): OrderPeriodStatus {
      return isIssueOrderPeriodOpenHelper({
        today,
        orderPeriod: {
          start: issue.orderPeriod && issue.orderPeriod.beginDate,
          end: issue.orderPeriod && issue.orderPeriod.endDate
        },
        repricingOrderPeriod: {
          start:
            issue.repricingOrderPeriod && issue.repricingOrderPeriod.beginDate,
          end: issue.repricingOrderPeriod && issue.repricingOrderPeriod.endDate
        }
      });
    }

    export function isIssueOrderPeriodOpenHelper({
      orderPeriod,
      repricingOrderPeriod,
      today
    }: {
      orderPeriod: {
        start: Moment | string | Date;
        end: Moment | string | Date;
      };
      repricingOrderPeriod: {
        start: Moment | string | Date;
        end: Moment | string | Date;
      };
      today: Moment;
    }): OrderPeriodStatus {
      const orderPeriodStatus: OrderPeriodStatus = {
        isOpen: false,
        isExpiring: false,
        isDateOverCountdown: false
      };
      if (orderPeriod && orderPeriod.start && orderPeriod.end) {
        const start: Moment = toMoment(orderPeriod.start);
        const end: Moment = toMoment(orderPeriod.end);

        orderPeriodStatus.isOpen =
          start.valueOf() < today.valueOf() && end.valueOf() > today.valueOf();
        orderPeriodStatus.isExpiring = isDateExpiring(end, today);
        orderPeriodStatus.isDateOverCountdown = moment(today).isBefore(start);
      }

      const repricingOrderPeriodStatus: OrderPeriodStatus = {
        isOpen: false,
        isExpiring: false,
        isDateOverCountdown: false
      };
      if (
        repricingOrderPeriod &&
        repricingOrderPeriod.start &&
        repricingOrderPeriod.end
      ) {
        const start: Moment = toMoment(repricingOrderPeriod.start);
        const end: Moment = toMoment(repricingOrderPeriod.end);

        repricingOrderPeriodStatus.isOpen =
          start.valueOf() < today.valueOf() && end.valueOf() > today.valueOf();
        repricingOrderPeriodStatus.isExpiring = isDateExpiring(end, today);
        repricingOrderPeriodStatus.isDateOverCountdown = moment(today).isBefore(
          start
        );
      }

      return orderPeriodStatus.isOpen
        ? orderPeriodStatus
        : repricingOrderPeriodStatus;
    }

    export function toCountdown(from: Moment, to: Moment) {
      return moment(from, 'DD/MM/YYYY HH:mm:ss').diff(
        moment(to, 'DD/MM/YYYY HH:mm:ss')
      );
    }

    export function getIssueOrderPeriodCountdownForCompetitive(
      issue: { saleDate: Moment },
      today: Moment
    ): OrderPeriodCountdown {
      const {
        isOpen,
        isExpiring,
        isDateOverCountdown
      } = isIssueOrderPeriodOpenForCompetitive(issue, today);

      return isOpen
        ? {
          isOpen,
          isExpiring,
          isDateOverCountdown,
          countdown: toDailyCountdown(issue.saleDate, today),
          type: null,
          orderTypes: null
        }
        : {
          isOpen,
          isExpiring,
          isDateOverCountdown,
          countdown: undefined,
          type: null,
          orderTypes: null
        };
    }

    export function isIssueOrderPeriodOpenForCompetitive(
      issue: { saleDate: Moment },
      today: Moment
    ): OrderPeriodStatus {
      return isIssueOrderPeriodOpenHelperForCompetitive(issue.saleDate, today);
    }

    export function isIssueOrderPeriodOpenHelperForCompetitive(
      saleDt: Moment,
      today: Moment
    ): OrderPeriodStatus {
      const saleDate: Moment = moment(saleDt, 'YYYY-MM-DD HH:mm').local();

      if (moment(saleDate).isSame(moment(today).startOf('day'), 'd')) {
        if (isDateExpiring(saleDate, today)) {
          return {
            isOpen: true,
            isExpiring: true,
            isDateOverCountdown: false
          };
        }
        else if (moment(saleDate).isAfter(today)) {
          return {
            isOpen: true,
            isExpiring: false,
            isDateOverCountdown: false
          };
        }
      }

      return {
        isOpen: false,
        isExpiring: false,
        isDateOverCountdown: moment(saleDate).isAfter(
          moment(today).startOf('day'),
          'd'
        )
      };
    }

    export function toDailyCountdown(from: Moment, to: Moment): number {
      return moment(from, 'MM-DD-YYYY hh:mm:ss').diff(
        moment(to, 'MM-DD-YYYY hh:mm:ss')
      );
    }

  }

  export function isDateExpiring(
    date: Moment,
    today: Moment,
    expirationTimeSpan: number = 5
  ) {
    return (
      moment(date).isBefore(moment(today).add(expirationTimeSpan, 'minutes')) &&
      moment(date).isAfter(today)
    );
  }

  export function getStartTimeTz(deal: DealVersion) {
    switch (deal && deal.dealType) {
      case DealType.competitive:
        return deal.saleDate
          ? IssueDate.toStartTimeTz(deal.saleDate, IssueDate.Formats.hmmaz)
          : '';
      case DealType.negotiated: {
        const date: Moment | undefined =
          (deal.repricingOrderPeriod && deal.repricingOrderPeriod.beginDate) ||
          (deal.orderPeriod && deal.orderPeriod.beginDate);
        return date
          ? IssueDate.toStartTimeTz(date, IssueDate.Formats.hmmaz)
          : '';
      }
      default:
        throw new Error(`${deal.dealType} deal type is wrong`);
    }
  }

  export function orderPeriodIsOpen(issue: DealVersion): OrderPeriodStatus {
    const today: Moment = moment();
    const isGamedayEnabled = issue.gamedaySettings?.enabled ?? false;

    switch (issue.dealType) {
      case DealType.competitive:
        return isGamedayEnabled
          ? DealOrderType.isIssueOrderPeriodOpen(issue, today)
          : DealOrderType.isIssueOrderPeriodOpenForCompetitive(issue, today);
      case DealType.negotiated:
      case DealType.privatePlacement:
        return DealOrderType.isIssueOrderPeriodOpen(issue, today);
      default:
        throw new Error(`${issue.dealType} deal type is wrong`);
    }
  }

  export function getCountdown(issue: DealVersion): OrderPeriodCountdown {
    const today: Moment = moment();
    const isGamedayEnabled = issue.gamedaySettings?.enabled ?? false;

    switch (issue.dealType) {
      case DealType.competitive:
        return isGamedayEnabled
          ? DealOrderType.getIssueOrderPeriodCountdown(issue, today)
          : DealOrderType.getIssueOrderPeriodCountdownForCompetitive(issue, today);
      case DealType.negotiated:
      case DealType.privatePlacement:
        return DealOrderType.getIssueOrderPeriodCountdown(issue, today);
      default:
        throw new Error(`${issue.dealType} deal type is wrong`);
    }
  }

}
